// Configurações da API SIAPESUS
// Centraliza todas as configurações de conexão com a API

import APP_CONFIG from './appConfig';

export const getApiBaseUrl = (fallback = 'http://localhost:3003') => {
  return APP_CONFIG.API_URL || fallback;
};

const API_CONFIG = {
  // URL base da API
  // DESENVOLVIMENTO: localhost:3003 (nova API com BPA de embarques)
  BASE_URL: getApiBaseUrl('http://localhost:3003'),
  
  // Endpoints da API
  ENDPOINTS: {
    // Autenticação
    AUTH: {
      LOGIN: '/api/auth/login',
      VERIFY: '/api/auth/verify',
      PROFILE: '/api/auth/profile',
      LOGOUT: '/api/auth/logout'
    },
    
    // Consultas gerais
    QUERIES: {
      UNIDADES: '/api/queries/unidades',
      PROFISSIONAIS: '/api/queries/profissionais',
      PROCEDIMENTOS: '/api/queries/procedimentos',
      CATEGORIAS: '/api/queries/categorias',
      PRODUCAO_BPA: '/api/queries/producao-bpa'
    },
    
    // BPA
    BPA: {
      DADOS_PRODUCAO: '/api/bpa/dados-producao',
      GERAR: '/api/bpa/gerar',
      BUSCAR_GERADOS: '/api/bpa/buscar-gerados',
      TRANSPORTE_EMBARQUES: '/api/bpa/transporte/embarques',
      TRANSPORTE_RELATORIO: '/api/bpa/transporte/relatorio',
      TRANSPORTE_GERAR: '/api/bpa/transporte/gerar'
    },
    
    // Cabeçalhos BPA
    BPA_CABECALHO: {
      BUSCAR: '/api/bpa/cabecalhos',
      DELETAR: '/api/bpa/cabecalhos',
      GERAR_BPA: '/api/bpa/cabecalhos'
    },
    
    // Usuários
    USERS: {
      LIST: '/api/users',
      GET: '/api/users',
      CREATE: '/api/users',
      UPDATE: '/api/users',
      DELETE: '/api/users',
      MENUS: '/api/users/menus/list',
      ROLES: '/api/users'
    },

    // Transport
    TRANSPORT: {
      BASE: '/api/transport',
      RESOURCES: '/api/transport/resources',
      ESUS_CIDADAO: '/api/transport/esus/cidadao',
      REPORTS: '/api/transport/reports',
      REPORTS_PDF: '/api/transport/reports/pdf'
    },
    
    // Health check
    HEALTH: '/health'
  },
  
  // Configurações de timeout
  TIMEOUT: {
    DEFAULT: 30000, // 30 segundos
    UPLOAD: 60000,  // 60 segundos para uploads
    DOWNLOAD: 120000 // 2 minutos para downloads
  },
  
  // Configurações de retry
  RETRY: {
    ATTEMPTS: 3,
    DELAY: 1000 // 1 segundo
  },
  
  // Headers padrão
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  
  // Configurações de paginação
  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_LIMIT: 10,
    MAX_LIMIT: 100
  },
  
  // Configurações de cache
  CACHE: {
    ENABLED: true,
    TTL: 5 * 60 * 1000, // 5 minutos
    MAX_SIZE: 50 // máximo 50 itens em cache
  }
};

// Função para construir URL completa
export const buildApiUrl = (endpoint) => {
  // Remove barras duplicadas
  const baseUrl = API_CONFIG.BASE_URL.replace(/\/+$/, ''); // Remove / do final
  const endpointClean = endpoint.replace(/^\/+/, '/'); // Garante / no início
  const finalUrl = `${baseUrl}${endpointClean}`;
  
  return finalUrl;
};

// Função para obter headers com autenticação
export const getAuthHeaders = (token) => {
  return {
    ...API_CONFIG.DEFAULT_HEADERS,
    'Authorization': `Bearer ${token}`
  };
};

// Função para construir parâmetros de query
export const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item));
      } else {
        searchParams.append(key, value);
      }
    }
  });
  
  return searchParams.toString();
};

// Função para construir URL com query parameters
export const buildUrlWithParams = (endpoint, params = {}) => {
  const baseUrl = buildApiUrl(endpoint);
  const queryString = buildQueryParams(params);
  
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// Função para fazer requisição com retry
export const fetchWithRetry = async (url, options = {}, retries = API_CONFIG.RETRY.ATTEMPTS) => {
  try {
    const response = await fetch(url, {
      ...options,
      timeout: API_CONFIG.TIMEOUT.DEFAULT
    });
    
    // 401/403 seguem para o apiService tratar token. Demais erros de cliente
    // (400, 404, 422) trazem a mensagem da API e não devem sofrer retry.
    if (!response.ok && response.status !== 401 && response.status !== 403) {
      let apiMessage = '';
      try {
        const body = await response.json();
        apiMessage = body.error || body.message || '';
      } catch (_) {
        apiMessage = '';
      }
      const error = new Error(apiMessage || `HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      throw error;
    }
    
    return response;
  } catch (error) {
    if (
      error.message.includes('401') ||
      error.message.includes('403') ||
      error.message.includes('Token') ||
      error.status === 400 ||
      error.status === 404 ||
      error.status === 409 ||
      error.status === 422
    ) {
      throw error;
    }
    
    if (retries > 0) {
      console.warn(`Tentativa falhou, tentando novamente... (${retries} tentativas restantes)`);
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY.DELAY));
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
};

// Cache simples em memória
const cache = new Map();

// Função para fazer requisição com cache
export const fetchWithCache = async (url, options = {}, useCache = API_CONFIG.CACHE.ENABLED) => {
  // Não usar cache para requisições autenticadas (para evitar problemas com token expirado)
  const isAuthenticated = options.headers && options.headers.Authorization;
  if (!useCache || isAuthenticated) {
    return fetchWithRetry(url, options);
  }
  
  const cacheKey = `${url}_${JSON.stringify(options)}`;
  const cached = cache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < API_CONFIG.CACHE.TTL) {
    return cached.data;
  }
  
  try {
    const response = await fetchWithRetry(url, options);
    const data = await response.json();
    
    // Limpar cache se exceder o tamanho máximo
    if (cache.size >= API_CONFIG.CACHE.MAX_SIZE) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    
    cache.set(cacheKey, {
      data: { ...response, json: () => Promise.resolve(data) },
      timestamp: Date.now()
    });
    
    return { ...response, json: () => Promise.resolve(data) };
  } catch (error) {
    console.error('Erro na requisição:', error);
    throw error;
  }
};

// Função para limpar cache
export const clearCache = () => {
  cache.clear();
};

// Função para obter configurações de paginação
export const getPaginationConfig = (page = API_CONFIG.PAGINATION.DEFAULT_PAGE, limit = API_CONFIG.PAGINATION.DEFAULT_LIMIT) => {
  return {
    page: Math.max(1, parseInt(page)),
    limit: Math.min(API_CONFIG.PAGINATION.MAX_LIMIT, Math.max(1, parseInt(limit)))
  };
};

// Função para validar resposta da API
export const validateApiResponse = (response) => {
  if (!response || typeof response !== 'object') {
    throw new Error('Resposta inválida da API');
  }
  
  if (!response.success) {
    const errorMessage = response.error || 'Erro desconhecido da API';
    // Não lançar erro aqui se for erro de token - deixar o apiService tratar
    throw new Error(errorMessage);
  }
  
  return response;
};

// Função para tratar erros de API
export const handleApiError = (error) => {
  console.error('Erro da API:', error);
  
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return 'Erro de conexão com o servidor. Verifique sua internet.';
  }
  
  if (error.message.includes('401')) {
    return 'Sessão expirada. Faça login novamente.';
  }
  
  if (error.message.includes('403')) {
    return 'Acesso negado. Verifique suas permissões.';
  }
  
  if (error.message.includes('404')) {
    return 'Recurso não encontrado.';
  }
  
  if (error.message.includes('500')) {
    return 'Erro interno do servidor. Tente novamente mais tarde.';
  }

  if (error.message.includes('HTTP 400')) {
    return 'Não foi possível salvar. Verifique os dados e tente novamente.';
  }
  
  return error.message || 'Erro desconhecido';
};

// Configurações específicas para diferentes ambientes
export const ENVIRONMENT_CONFIG = {
  development: {
    BASE_URL: getApiBaseUrl('http://localhost:3003'),
    CACHE_ENABLED: false,
    LOG_LEVEL: 'debug'
  },
  
  production: {
    BASE_URL: getApiBaseUrl('https://api.siapesus.com.br'),
    CACHE_ENABLED: true,
    LOG_LEVEL: 'error'
  },
  
  test: {
    BASE_URL: getApiBaseUrl('http://localhost:3003'),
    CACHE_ENABLED: false,
    LOG_LEVEL: 'silent'
  }
};

// Obter configuração do ambiente atual
export const getCurrentEnvironment = () => {
  return process.env.NODE_ENV || 'development';
};

// Obter configuração específica do ambiente
export const getEnvironmentConfig = () => {
  const env = getCurrentEnvironment();
  return ENVIRONMENT_CONFIG[env] || ENVIRONMENT_CONFIG.development;
};

// Aplicar configurações do ambiente
export const applyEnvironmentConfig = () => {
  const envConfig = getEnvironmentConfig();
  
  // Atualizar configurações baseadas no ambiente
  API_CONFIG.BASE_URL = envConfig.BASE_URL;
  API_CONFIG.CACHE.ENABLED = envConfig.CACHE_ENABLED;
};

// Inicializar configurações
applyEnvironmentConfig();

export default API_CONFIG;
